import React from 'react';
import { Wifi, WifiOff, RefreshCw, Cloud, CloudOff } from 'lucide-react';

// Hook for offline status
export const useOfflineStatus = () => {
  const [isOnline, setIsOnline] = React.useState(navigator.onLine);
  const [pendingCount, setPendingCount] = React.useState(0);
  const [isSyncing, setIsSyncing] = React.useState(false);

  React.useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setIsSyncing(true);
      // Auto sync will happen
    };
    
    const handleOffline = () => setIsOnline(false);
    
    const handleSyncComplete = (e) => {
      setIsSyncing(false);
      setPendingCount(0);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('stature:sync-complete', handleSyncComplete);
    window.addEventListener('stature:online', () => setIsSyncing(true));

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('stature:sync-complete', handleSyncComplete);
    };
  }, []);

  const incrementPending = () => setPendingCount(prev => prev + 1);

  return { isOnline, pendingCount, isSyncing, incrementPending };
};

// Offline indicator component
export const OfflineIndicator = () => {
  const { isOnline, pendingCount, isSyncing } = useOfflineStatus();

  if (isOnline && pendingCount === 0 && !isSyncing) {
    return null; // Don't show anything when online with no pending
  }

  return (
    <div 
      className={`fixed top-0 left-0 right-0 z-50 px-4 py-2 text-center text-sm font-medium transition-all ${
        !isOnline 
          ? 'bg-amber-500 text-white' 
          : isSyncing 
            ? 'bg-blue-500 text-white'
            : pendingCount > 0 
              ? 'bg-teal-500 text-white'
              : ''
      }`}
    >
      {!isOnline ? (
        <div className="flex items-center justify-center gap-2">
          <WifiOff className="w-4 h-4" />
          <span>Sin conexión - Los cambios se guardarán localmente</span>
        </div>
      ) : isSyncing ? (
        <div className="flex items-center justify-center gap-2">
          <RefreshCw className="w-4 h-4 animate-spin" />
          <span>Sincronizando datos...</span>
        </div>
      ) : pendingCount > 0 ? (
        <div className="flex items-center justify-center gap-2">
          <Cloud className="w-4 h-4" />
          <span>{pendingCount} cambio(s) pendiente(s) de sincronizar</span>
        </div>
      ) : null}
    </div>
  );
};

// Offline-aware fetch wrapper
export const offlineFetch = async (url, options = {}, offlineService) => {
  const isOnline = navigator.onLine;

  if (isOnline) {
    try {
      const response = await fetch(url, {
        ...options,
        credentials: 'include'
      });
      return response;
    } catch (error) {
      // Network error, might be offline now
      if (offlineService && options.method !== 'GET') {
        await offlineService.queueAction({
          type: options.actionType || 'unknown',
          url,
          method: options.method,
          body: options.body ? JSON.parse(options.body) : null
        });
      }
      throw error;
    }
  } else {
    // Offline - queue the action for later
    if (offlineService && options.method !== 'GET') {
      await offlineService.queueAction({
        type: options.actionType || 'unknown',
        url,
        method: options.method,
        body: options.body ? JSON.parse(options.body) : null
      });
      
      // Return a mock successful response for writes
      return {
        ok: true,
        json: async () => ({ message: 'Queued for sync', offline: true })
      };
    }
    
    // For GET requests, try to get from cache
    if (offlineService && options.method === 'GET') {
      const cached = await offlineService.getCachedData(url);
      if (cached) {
        return {
          ok: true,
          json: async () => cached
        };
      }
    }
    
    throw new Error('Offline and no cached data available');
  }
};

export default { useOfflineStatus, OfflineIndicator, offlineFetch };
